import React, { useState } from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableContainer from '@material-ui/core/TableContainer'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter'
import { Formik, Field } from 'formik'
import {
  Grid,
  FormControlLabel,
  Button,
  Checkbox,
  Tooltip,
} from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../messages'
import { SiRNAProperties, ActiveLinkType, SemiConfigProperties } from '../enum'
import { ProductDetails, configContext } from '../Sirna'
import { useGetSubItemPriceSiRnaLazyQuery } from '@src/queries/PredesignedSiRNAQuery.generated'
import { useSemiConfSaveToSessionMutation } from '@src/mutations/SemiconfigSirnaMutation.generated'
import { sirnaRoute, useRouter } from '@src/routes'
import { priceFormatter } from '../../../../Configurators/utils/PriceFormatter'
import CircleLoader from '@src/components/CircleLoader/CircleLoader'
import ProductDetailsLink from '@src/routes/SemiConfigurators/components/ProductDetailsLink'
import { SemiConfigPropertyValues } from '../../../utils/constants'
import PerPage from '@src/routes/SemiConfigurators/products/sirna/Step1/PerPage'
import InfoIcon from '@material-ui/icons/Info'
import styles from '@src/routes/SemiConfigurators/utils/styles'
import PaginationForSirna from './PaginationForSirna'
import { SubSetInfo, configurableOptions } from '../Reducer'

interface Column {
  id: 'productNumber' | 'SeqType' | 'species' | 'geneSymbol' | 'pricing'
  label: string
  minWidth?: number
  align?: 'right'
  format?: (value: number) => string
}

interface SubColumn {
  id: 'siRNAID' | 'SeqStart' | 'Purification' | 'Size' | 'Price'
  label: string
  minWidth?: number
  align?: 'right'
  format?: (value: number) => string
}

const columns: Column[] = [
  { id: 'productNumber', label: 'Product Number', minWidth: 80 },
  { id: 'SeqType', label: 'siRNA Type', minWidth: 100 },
  { id: 'geneSymbol', label: 'Gene Symbol', minWidth: 60 },
  { id: 'species', label: 'Species', minWidth: 60 },
  { id: 'pricing', label: 'Pricing', minWidth: 50, align: 'right' },
]

const subColumn: SubColumn[] = [
  { id: 'siRNAID', label: 'siRNA ID', minWidth: 80 },
  { id: 'SeqStart', label: 'Sequence Start', minWidth: 100 },
  { id: 'Purification', label: 'Purification', minWidth: 60 },
  { id: 'Size', label: 'Size', minWidth: 50 },
  { id: 'Price', label: 'Price', minWidth: 50, align: 'right' },
]

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      paddingRight: theme.spacing(5),
      paddingLeft: theme.spacing(5),
    },
    boldText: {
      fontWeight: theme.typography.fontWeightBold,
    },
    container: {
      maxHeight: 440,
    },
    fontBold: {
      borderBottom: 1,
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightMedium,
      width: theme.typography.pxToRem(125),
    },
    lightGreyFont: {
      color: theme.palette.text.primary,
      borderBottom: 1,
      fontSize: theme.typography.pxToRem(14),
    },
    textAlignCentre: {
      textAlign: 'center',
    },
    paginationPadding: {
      paddingTop: theme.spacing(2.5),
      paddingRight: theme.spacing(70),
    },
    tableHead: {
      color: theme.palette.text.primary,
      fontSize: theme.typography.pxToRem(14),
    },
    subTableHeading: {
      color: theme.palette.text.primary,
      fontSize: theme.typography.pxToRem(14),
      '&:first-child': {
        paddingLeft: theme.spacing(11),
      },
    },
    supScript: {
      verticalAlign: 'top',
    },
    dropDown: {
      paddingRight: theme.spacing(10),
    },
    togglePadding: {
      paddingRight: theme.spacing(2),
    },
    subTableCss: {
      padding: 0,
      borderBottom: 0,
      backgroundColor: theme.palette.background.grey,
      '& th': {
        backgroundColor: theme.palette.background.grey,
      },
    },
    removeBorder: {
      '& td': {
        borderBottom: 0,
      },
      backgroundColor: theme.palette.background.grey,
    },
    nextButtonPadding: {
      paddingTop: theme.spacing(13),
    },
    nextButton: {
      minWidth: 220,
      height: theme.spacing(12),
    },
    tableContainer: {
      overflowX: 'hidden',
    },
    InfoPopup: {
      maxWidth: theme.spacing(105),
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      boxShadow: theme.shadows[4],
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.common.black,
      borderRadius: '2%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    InfoIcon: {
      position: 'relative',
      fontSize: theme.typography.pxToRem(16),
      top: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
    },
    arrow1: {
      color: theme.palette.common.white,
    },
    IconButton: {
      outlined: true,
      padding: theme.spacing(0.4, 1, 1, 1),
      marginBottom: theme.spacing(1),
    },
    icon: {
      fontSize: theme.typography.pxToRem(16),
      verticalAlign: 'middle',
    },
    lightOrangeFont: {
      backgroundColor: '#ff8d00',
      color: '#ffffff',
      fontSize: theme.typography.pxToRem(10),
      padding: theme.spacing(1),
    },
    hideRow: {
      display: 'none',
      fontWeight: theme.typography.fontWeightMedium,
      color: '#535353',
    },
    tableContainerScrollBar: {
      maxHeight: 604,
      overflowX: 'hidden',
    },
  }
})

interface SearchTableProps {
  productDetails: ProductDetails
  currencyFormat: string
  thousandSeparator: any
  thousandSpacing: any
  prefixCurrecny: any
  decimalSeparator: any
}

const SearchTable: React.FC<SearchTableProps> = (props) => {
  const classes = useStyles()
  const {
    productDetails,
    currencyFormat,
    thousandSeparator,
    thousandSpacing,
    prefixCurrecny,
    decimalSeparator,
  } = props
  const { state, dispatch } = React.useContext(configContext)
  const intl = useIntl()
  const maxItems = SemiConfigPropertyValues.MaximumItemsAllowed
  const { siRNAData, searchResult, selectedData } = state

  const [getSubItemPriceSiRNA, subItemPriceSiRNAResult] =
    useGetSubItemPriceSiRnaLazyQuery({
      fetchPolicy: 'network-only',
    })

  const router = useRouter()
  const confirmOrder = `${sirnaRoute.index()}?activeLink=${
    ActiveLinkType.confirmOrder
  }`
  const [detailsExpanded, setDetailsExpanded] = useState(
    Array(siRNAData.length).fill(false)
  )
  const [toggle, setToggle] = React.useState(false)
  const [scrollBar, setScrollBar] = React.useState(false)
  const globalCSS = styles()

  const FetchSubItemPriceData = async (obj): Promise<void> => {
    await getSubItemPriceSiRNA({
      variables: {
        input: obj,
      },
    })
  }

  const calculatePrice = (): void => {
    let price = 0
    let isPriceValid = true
    let subtotal: any = 0
    if (selectedData) {
      const selectedSubSetList: any[] = []
      selectedData?.forEach((item: any) => {
        selectedSubSetList.push(...item.subSetInfo)
      })
      selectedSubSetList.forEach((item) => {
        if (
          item.count > 0 &&
          item.price &&
          item.price !== SemiConfigProperties.Quote &&
          item.price !== '--'
        ) {
          price = price + parseFloat(item.price)
        } else if (item.price === SemiConfigProperties.Quote) {
          subtotal = intl.formatMessage(messages._PC_QUOTE_ITEM)
          isPriceValid = false
        } else {
          subtotal = '--'
          isPriceValid = false
        }
      })
      if (price !== null && isPriceValid) {
        const formattedPrice = priceFormatter(
          price,
          currencyFormat,
          thousandSeparator,
          thousandSpacing,
          decimalSeparator,
          prefixCurrecny,
          selectedSubSetList[0]?.currencySymbol
        )
        subtotal = formattedPrice
        dispatch({
          type: 'UPDATE_TOTAL_PRICE',
          data: subtotal,
        })
      }
    }
  }

  React.useEffect(() => {
    let detailsToggle: any = []
    detailsToggle = Array(siRNAData.length).fill(false)
    setDetailsExpanded(detailsToggle)

    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.paginationData.page, state.perPageCount])

  React.useEffect(() => {
    if (
      subItemPriceSiRNAResult &&
      subItemPriceSiRNAResult?.data &&
      subItemPriceSiRNAResult?.data?.getSubItemPriceSiRNA &&
      subItemPriceSiRNAResult?.data?.getSubItemPriceSiRNA?.result
    ) {
      const subitemResults =
        subItemPriceSiRNAResult?.data?.getSubItemPriceSiRNA?.result
      const completeData = siRNAData.map((singleData) => {
        const subsetData: SubSetInfo[] = singleData.subSetInfo.map(
          (singleSubSetData) => {
            const indexData = subitemResults.findIndex((res) => {
              return res?.siRNAId === singleSubSetData.siRNAId
            })
            if (indexData !== -1) {
              return {
                ...singleSubSetData,
                purification: subitemResults[indexData]?.purification || '',
                size: subitemResults[indexData]?.size || '',
                price: subitemResults[indexData]?.price || '',
                currency: subitemResults[indexData]?.currency || '',
                currencySymbol: subitemResults[indexData]?.currencySymbol || '',
                material: subitemResults[indexData]?.material || '',
              }
            } else return singleSubSetData
          }
        )
        singleData.subSetInfo = subsetData
        return singleData
      })
      if (selectedData && selectedData.length > 0) {
        const updatedSelectedData = selectedData?.map((singleData) => {
          const subsetData: SubSetInfo[] = singleData.subSetInfo.map(
            (singleSubSetData) => {
              const indexData = subitemResults.findIndex((res) => {
                return res?.siRNAId === singleSubSetData.siRNAId
              })
              if (indexData !== -1) {
                return {
                  ...singleSubSetData,
                  purification: subitemResults[indexData]?.purification || '',
                  size: subitemResults[indexData]?.size || '',
                  price: subitemResults[indexData]?.price || '',
                  currency: subitemResults[indexData]?.currency || '',
                  currencySymbol:
                    subitemResults[indexData]?.currencySymbol || '',
                  material: subitemResults[indexData]?.material || '',
                }
              } else return singleSubSetData
            }
          )
          singleData.subSetInfo = subsetData
          return singleData
        })
        dispatch({
          type: 'UPDATE_SELECTED_DATA',
          data: updatedSelectedData,
        })
      }

      dispatch({
        type: 'UPDATE_DATA',
        data: [...completeData],
      })
    }
  }, [subItemPriceSiRNAResult])

  React.useEffect(() => {
    if (selectedData && selectedData.length > 0) {
      calculatePrice()
    } else {
      dispatch({
        type: 'UPDATE_TOTAL_PRICE',
        data: '',
      })
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData])

  React.useEffect(() => {
    const ItemListWithCheckStatus = siRNAData.map((item) => {
      return {
        refseqId: item.refseqId,
        siRNAType: item.siRNAType,
        species: item.species,
        subSetInfoData: item.subSetInfo,
        subSetInfo: item.subSetInfo.filter((sirna: any) => sirna.checked),
      }
    })
    const selectedItemsList = ItemListWithCheckStatus.filter(
      (item) => !!item.subSetInfo.length
    )

    const selectedSubSetList: any[] = []
    selectedItemsList?.forEach((item: any) => {
      selectedSubSetList.push(...item.subSetInfo)
    })

    if (selectedSubSetList?.length) {
      dispatch({
        type: 'UPDATE_ADD_TO_CART_DATA',
        data: {
          ...state.sirnaAddtoCartData,
          results: selectedSubSetList.map((item: any) => {
            return {
              siRNAId: item.siRNAId.toString(),
              geneId: item.geneId.toString(),
              geneSymbol: item.geneSymbol.toString(),
              refseqId: item.refseqId.toString(),
              species: item.species.toString(),
              method: item.method.toString(),
              sirnaProdType: item.sirnaProdType.toString(),
              siRNAType: item.siRNAType.toString(),
              siRNAAntiSenseSeq: item.siRNAAntiSenseSeq.toString(),
              siRNASenseSeq: item.siRNASenseSeq.toString(),
              fuzzyStart: item.fuzzyStart.toString(),
              siRNATargetSeq: item.siRNATargetSeq.toString(),
              fuzzyEnd: item.fuzzyEnd.toString(),
              ranking: item.ranking.toString(),
              refseqStart: item.refseqStart.toString(),
              refseqEnd: item.refseqEnd.toString(),
              purification: item?.purification?.toString(),
              size: item?.size?.toString(),
            }
          }),
          country: props.productDetails.region,
        },
      })
    }
    if (!siRNAData.length) {
      let detailsToggle: any = []
      detailsToggle = Array(siRNAData.length).fill(false)
      setDetailsExpanded(detailsToggle)
    } else if (selectedItemsList.length > 0) {
      const allSubItems: any[] = []
      selectedItemsList.map((item) => allSubItems.push(item.subSetInfoData))

      // load price for selected set
      const subSetItems = allSubItems.flat().map((subItem) => ({
        siRNAId: subItem.siRNAId,
        purification: subItem.purification,
        size: subItem.size,
        siRNAType: subItem.siRNAType,
      }))

      FetchSubItemPriceData({
        countryCode: productDetails.region,
        subSetItems,
      })
      // open selected set

      let detailsToggle: any = []
      detailsToggle = ItemListWithCheckStatus.map((item) => {
        return !!item.subSetInfo.length
      })
      setDetailsExpanded(detailsToggle)
    }
    if (siRNAData.length > 10) {
      setScrollBar(true)
    } else {
      setScrollBar(false)
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult])

  const handlePurificationDropDown = (
    event,
    siRNAIndex,
    subSetInfoIndex
  ): void => {
    if (state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
    }
    const { value } = event.target
    if (value) {
      siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].purification = value
      FetchSubItemPriceData({
        countryCode: productDetails.region,
        subSetItems: [
          {
            siRNAId: siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].siRNAId,
            purification: `${siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].purification}`,
            size: `${siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].size}`,
            siRNAType:
              siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].siRNAType,
          },
        ],
      })
    }
  }

  const handleSizeDropDown = (event, siRNAIndex, subSetInfoIndex): void => {
    if (state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
    }
    const { value } = event.target
    if (value) {
      siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].size = value
      FetchSubItemPriceData({
        countryCode: productDetails.region,
        subSetItems: [
          {
            siRNAId: siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].siRNAId,
            purification: `${siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].purification}`,
            size: `${siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].size}`,
            siRNAType:
              siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].siRNAType,
          },
        ],
      })
    }
  }

  const handleCheckBoxChange = (
    event,
    siRNAIndex,
    itemId,
    subSetInfoIndex
  ): void => {
    if (state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
    }
    const { checked } = event.target
    siRNAData[siRNAIndex].subSetInfo[subSetInfoIndex].checked = checked
    const singleSelectedData = {
      refseqId: siRNAData[siRNAIndex].refseqId,
      siRNAType: siRNAData[siRNAIndex].siRNAType,
      species: siRNAData[siRNAIndex].species,
      subSetInfo: siRNAData[siRNAIndex].subSetInfo.filter(
        (sirna: any) => sirna.checked
      ),
    }
    const currentSelectedData = selectedData ? [...selectedData] : []
    const updatedSelectedData = currentSelectedData.filter(
      (item) => item.refseqId !== singleSelectedData.refseqId
    )
    if (singleSelectedData?.subSetInfo.length > 0)
      updatedSelectedData.push(singleSelectedData)
    dispatch({
      type: 'UPDATE_SELECTED_DATA',
      data: updatedSelectedData,
    })
    dispatch({
      type: 'UPDATE_DATA',
      data: [...state.siRNAData],
    })
    dispatch({
      type: 'UPDATE_CHECKED_ALL',
      data: false,
    })
  }

  const [semiConfSaveToSession] = useSemiConfSaveToSessionMutation()

  const saveSessionHandler = async (): Promise<void> => {
    await semiConfSaveToSession({
      variables: {
        input: {
          results: searchResult.results,
          term: searchResult.term,
          count: searchResult.count,
          selectedItems: selectedData,
        },
        productInput: productDetails,
      },
    })
    router.push(confirmOrder)
  }

  const handleNextStep = (): void => {
    if (!state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: false })
    }
    if (selectedData?.length === 0) {
      dispatch({
        type: 'UPDATE_SEARCH_ERROR',
        data: true,
      })
      window.scrollTo(0, 0)
    } else if (selectedData && selectedData?.length > 500) {
      dispatch({
        type: 'UPDATE_SEARCH_ERROR',
        data: false,
      })
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.SEMICONF_MAX_ITEMS_ALLOWED, {
          maxItems,
        }),
      })
      window.scrollTo(0, 0)
    } else {
      dispatch({
        type: 'UPDATE_SEARCH_ERROR',
        data: false,
      })
      saveSessionHandler()
    }
  }

  const handleDetailsClick = (e: React.MouseEvent): void => {
    e.preventDefault()
    setDetailsExpanded(detailsExpanded)
    setToggle(!toggle)
  }

  const renderSubSetInfo = (item, siRNAIndex) => {
    const result: any[] = []
    item.subSetInfo.map((info, subSetInfoIndex) => {
      let priceStr = ''
      let sirnaType = ''

      if (info.siRNAType === SiRNAProperties.Validated) {
        sirnaType = SiRNAProperties.ValidatedLabel
      } else if (info.siRNAType === SiRNAProperties.Positive) {
        sirnaType = SiRNAProperties.PositiveControl
      }

      if (!info.price) {
        priceStr = ActiveLinkType.priceDash
      } else if (info.price === SemiConfigProperties.Quote) {
        priceStr = SemiConfigProperties.Quote
      } else {
        priceStr = priceFormatter(
          parseFloat(info.price),
          currencyFormat,
          thousandSeparator,
          thousandSpacing,
          decimalSeparator,
          prefixCurrecny,
          info?.currencySymbol
        )
      }
      result.push(
        <>
          <TableRow>
            <TableCell>
              <FormControlLabel
                classes={{ label: classes.fontBold }}
                control={
                  <Checkbox
                    checked={info.checked}
                    value={info.siRNAId}
                    color="primary"
                    onChange={(e) => {
                      handleCheckBoxChange(
                        e,
                        siRNAIndex,
                        info.siRNAId,
                        subSetInfoIndex
                      )
                    }}
                  />
                }
                label={info.siRNAId}
              />
              {sirnaType ? (
                <span className={classes.lightOrangeFont}>
                  <FormattedMessage
                    {...messages.SEMICONF_VALIDATED_OR_POSITIVE_LABEL_sirna}
                    values={{ sirnaType }}
                  />
                </span>
              ) : (
                <span className={globalCSS.sirnaNoLabel}></span>
              )}
            </TableCell>
            <TableCell>
              <label className={classes.lightGreyFont}>{info.fuzzyStart}</label>
            </TableCell>
            <TableCell>
              <Field
                name="purification"
                component={LiquidSelectAdapter}
                value={info.purification}
                onClick={(e) => {
                  handlePurificationDropDown(e, siRNAIndex, subSetInfoIndex)
                }}
                options={configurableOptions.purification}
                className={classes.textAlignCentre}
              />
            </TableCell>
            <TableCell className={classes.dropDown}>
              <Field
                name="size"
                component={LiquidSelectAdapter}
                value={info.size}
                onClick={(e) => {
                  handleSizeDropDown(e, siRNAIndex, subSetInfoIndex)
                }}
                options={configurableOptions.size}
                className={classes.textAlignCentre}
              />
            </TableCell>
            <TableCell align="right">
              <label className={classes.lightGreyFont}>
                {priceStr === SemiConfigProperties.Quote ? (
                  <FormattedMessage {...messages._PC_QUOTE_ITEM} />
                ) : (
                  `${priceStr}`
                )}
              </label>
            </TableCell>
          </TableRow>
        </>
      )
      return item
    })
    return result
  }

  const renderData = () => {
    const result: any = []
    siRNAData.forEach((item, siRNAIndex) => {
      let panalSirnaType = ''

      if (item.siRNAType === SiRNAProperties.Validated) {
        panalSirnaType = SiRNAProperties.panelLabelValidated
      } else if (
        item.siRNAType === SiRNAProperties.Predicted ||
        item.siRNAType === SiRNAProperties.Positive
      ) {
        panalSirnaType = SiRNAProperties.PredictedLabel
      }
      result.push(
        <TableRow
          key={siRNAIndex}
          className={detailsExpanded[siRNAIndex] ? classes.removeBorder : ''}
        >
          <TableCell>
            <label className={classes.lightGreyFont}>{item.refseqId}</label>
          </TableCell>
          <TableCell>
            <label className={classes.lightGreyFont}>{panalSirnaType}</label>
          </TableCell>
          <TableCell>
            <label className={classes.lightGreyFont}>
              {item?.subSetInfo[0]?.geneSymbol}
            </label>
          </TableCell>
          <TableCell>
            <label className={classes.lightGreyFont}>{item.species}</label>
          </TableCell>
          <TableCell align="right" className={classes.togglePadding}>
            <ProductDetailsLink
              onClick={(ev) => {
                detailsExpanded[siRNAIndex] = !detailsExpanded[siRNAIndex]
                handleDetailsClick(ev)
                if (detailsExpanded[siRNAIndex]) {
                  const subSetItems = item.subSetInfo.map((subItem) => ({
                    siRNAId: subItem.siRNAId,
                    purification: subItem.purification,
                    size: subItem.size,
                    siRNAType: subItem.siRNAType,
                  }))
                  const countryCode = productDetails.region
                  FetchSubItemPriceData({
                    countryCode,
                    subSetItems,
                  })
                }
              }}
              detailsExpanded={detailsExpanded[siRNAIndex]}
              productType={ActiveLinkType.ProductName}
            />
          </TableCell>
        </TableRow>,
        <TableRow>
          <TableCell className={classes.subTableCss} colSpan={5}>
            <Table
              stickyHeader
              aria-label="sticky table"
              className={detailsExpanded[siRNAIndex] ? '' : classes.hideRow}
            >
              <TableHead>
                <TableRow>
                  {subColumn.map((column) => (
                    <TableCell
                      key={column.id}
                      style={{ minWidth: column.minWidth }}
                      className={classes.subTableHeading}
                      align={column.align}
                    >
                      {column.id === SiRNAProperties.SeqStart ? (
                        <strong className={classes.tableHead}>
                          {column.label}
                          <Tooltip
                            title={
                              <React.Fragment>
                                <div>
                                  <strong className={classes.boldText}>
                                    <FormattedMessage
                                      {...messages.SEMICONF_HELP_ICON_BOLDTEXT_SEQUENCE_START_sirna}
                                    />
                                  </strong>
                                  <FormattedMessage
                                    {...messages.SEMICONF_HELP_ICON_TEXT_SEQUENCE_START_sirna}
                                  />
                                  <sup>
                                    <FormattedMessage
                                      {...messages.SEMICONF_HELP_ICON_TEXT_SEQUENCE_START_SYMBOL_sirna}
                                    />
                                  </sup>
                                  <FormattedMessage
                                    {...messages.SEMICONF_HELP_ICON_TEXT_SEQUENCE_START_2_sirna}
                                  />
                                  <sup>
                                    <FormattedMessage
                                      {...messages.SEMICONF_HELP_ICON_TEXT_SEQUENCE_START_SYMBOL_sirna}
                                    />
                                  </sup>
                                  <FormattedMessage
                                    {...messages.SEMICONF_HELP_ICON_TEXT_SEQUENCE_START_3_sirna}
                                  />
                                </div>
                              </React.Fragment>
                            }
                            placement="bottom-end"
                            classes={{
                              tooltip: classes.InfoPopup,
                              arrow: classes.arrow1,
                            }}
                            arrow
                          >
                            <InfoIcon
                              color="primary"
                              aria-label="info"
                              className={classes.InfoIcon}
                            ></InfoIcon>
                          </Tooltip>
                        </strong>
                      ) : column.id === SiRNAProperties.Size ? (
                        <strong className={classes.tableHead}>
                          {column.label}
                          <Tooltip
                            title={
                              <React.Fragment>
                                <div>
                                  <FormattedMessage
                                    {...messages.SEMICONF_HELP_ICON_TEXT_SIZE_sirna}
                                  />
                                </div>
                              </React.Fragment>
                            }
                            placement="bottom-end"
                            classes={{
                              tooltip: classes.InfoPopup,
                              arrow: classes.arrow1,
                            }}
                            arrow
                          >
                            <InfoIcon
                              aria-label="info"
                              className={classes.InfoIcon}
                              color="primary"
                            ></InfoIcon>
                          </Tooltip>
                        </strong>
                      ) : (
                        <>{column.label}</>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{renderSubSetInfo(item, siRNAIndex)}</TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )
    })
    return result
  }

  return (
    <>
      <Formik
        initialValues={{ purification: 'Standard' }}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            actions.setSubmitting(false)
          }, 1000)
        }}
      >
        <TableContainer
          className={
            scrollBar ? classes.tableContainerScrollBar : classes.tableContainer
          }
        >
          <Paper className={classes.root}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      style={{ minWidth: column.minWidth }}
                      align={column.align}
                    >
                      {column.id === SiRNAProperties.SeqType ? (
                        <>
                          {column.label}
                          <Tooltip
                            title={
                              <React.Fragment>
                                <div>
                                  <strong className={classes.boldText}>
                                    <FormattedMessage
                                      {...messages.SEMICONF_HELP_ICON_BOLDTEXT_SIRNATYPE_MISSION_sirna}
                                    />

                                    <sup>
                                      <FormattedMessage
                                        {...messages.CONF_TRADEMARK}
                                      />
                                    </sup>
                                    <FormattedMessage
                                      {...messages.SEMICONF_HELP_ICON_BOLDTEXT_SIRNATYPE_2_sirna}
                                    />
                                  </strong>
                                  <FormattedMessage
                                    {...messages.SEMICONF_HELP_ICON_TEXT_SIRNATYPE_sirna}
                                  />
                                  <sup>
                                    <FormattedMessage
                                      {...messages.CONF_TRADEMARK}
                                    />
                                  </sup>
                                  <FormattedMessage
                                    {...messages.SEMICONF_HELP_ICON_TEXT_SIRNATYPE_2_sirna}
                                  />
                                </div>
                              </React.Fragment>
                            }
                            placement="bottom-end"
                            classes={{
                              tooltip: classes.InfoPopup,
                              arrow: classes.arrow1,
                            }}
                            arrow
                          >
                            <InfoIcon
                              color="primary"
                              aria-label="info"
                              className={classes.InfoIcon}
                            ></InfoIcon>
                          </Tooltip>
                        </>
                      ) : (
                        <>{column.label}</>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{renderData()}</TableBody>
            </Table>
          </Paper>
        </TableContainer>
      </Formik>

      <div>
        <Grid container>
          <Grid container>
            <Grid item xs={4}>
              <PerPage />
            </Grid>
            <Grid item xs={8} className={classes.paginationPadding}>
              <PaginationForSirna />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.nextButtonPadding}>
            <Button
              color="primary"
              variant="contained"
              className={classes.nextButton}
              onClick={handleNextStep}
            >
              <FormattedMessage {...messages.CONTINUE} />
            </Button>
          </Grid>
        </Grid>
      </div>
      {subItemPriceSiRNAResult.loading && <CircleLoader />}
    </>
  )
}

export default SearchTable
